import React, { useEffect, useRef, useState } from 'react';
import { CURRENCIES, CurrencyCode, getCurrencyMeta, useCurrency } from '../../contexts/CurrencyContext';
import { track } from '../../utils/analytics';

interface CurrencySwitcherProps {
  variant?: 'pill' | 'ghost';
  accentColor?: string;
}

const CurrencySwitcher: React.FC<CurrencySwitcherProps> = ({ variant = 'ghost', accentColor = '#38bdf8' }) => {
  const { currency, setCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = getCurrencyMeta(currency);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const choose = (code: CurrencyCode) => {
    setCurrency(code);
    track('theme-change', { currency: code }); // reusing nearest analytics event
    setOpen(false);
  };

  return (
    <div className={`curr-switcher curr-switcher--${variant}`} ref={ref}>
      <button
        type="button"
        className="curr-switcher-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={current.label}
      >
        <span className="curr-switcher-flag">{current.flag}</span>
        <span className="curr-switcher-code">{current.code}</span>
        <span className="curr-switcher-caret">▾</span>
      </button>

      {open && (
        <ul className="curr-switcher-menu" role="listbox">
          {CURRENCIES.map((c) => {
            const active = c.code === current.code;
            return (
              <li key={c.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  className={`curr-switcher-option${active ? ' curr-switcher-option--active' : ''}`}
                  style={active ? { color: accentColor } : undefined}
                  onClick={() => choose(c.code)}
                >
                  <span className="curr-switcher-flag">{c.flag}</span>
                  <span className="curr-switcher-symbol">{c.symbol}</span>
                  <span>{c.label}</span>
                  {active && <span className="curr-switcher-check" style={{ color: accentColor }}>✓</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CurrencySwitcher;
